import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to manage personalized news reports
 * - Supports three report types: 'federal', 'state', 'local'
 * - Generates a new report every 24 hours per type
 * - Stores all reports in localStorage for history (separated by type AND state)
 * - Uses location data for personalization
 * - Syncs across tabs via storage events
 */

const REPORT_STORAGE_KEY_PREFIX = 'personalized_news_reports';
const LAST_REPORT_TIME_KEY_PREFIX = 'last_report_generated_at';
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

// State name lookup for proper display
const STATE_NAMES = {
  alabama: 'Alabama',
  alaska: 'Alaska',
  arizona: 'Arizona',
  arkansas: 'Arkansas',
  california: 'California',
  colorado: 'Colorado',
  connecticut: 'Connecticut',
  delaware: 'Delaware',
  florida: 'Florida',
  georgia: 'Georgia',
  hawaii: 'Hawaii',
  idaho: 'Idaho',
  illinois: 'Illinois',
  indiana: 'Indiana',
  iowa: 'Iowa',
  kansas: 'Kansas',
  kentucky: 'Kentucky',
  louisiana: 'Louisiana',
  maine: 'Maine',
  maryland: 'Maryland',
  massachusetts: 'Massachusetts',
  michigan: 'Michigan',
  minnesota: 'Minnesota',
  mississippi: 'Mississippi',
  missouri: 'Missouri',
  montana: 'Montana',
  nebraska: 'Nebraska',
  nevada: 'Nevada',
  newhampshire: 'New Hampshire',
  newjersey: 'New Jersey',
  newmexico: 'New Mexico',
  newyork: 'New York',
  northcarolina: 'North Carolina',
  northdakota: 'North Dakota',
  ohio: 'Ohio',
  oklahoma: 'Oklahoma',
  oregon: 'Oregon',
  pennsylvania: 'Pennsylvania',
  rhodeisland: 'Rhode Island',
  southcarolina: 'South Carolina',
  southdakota: 'South Dakota',
  tennessee: 'Tennessee',
  texas: 'Texas',
  utah: 'Utah',
  vermont: 'Vermont',
  virginia: 'Virginia',
  washington: 'Washington',
  westvirginia: 'West Virginia',
  wisconsin: 'Wisconsin',
  wyoming: 'Wyoming',
  districtofcolumbia: 'District of Columbia',
  dc: 'District of Columbia',
  puertorico: 'Puerto Rico'
};

// Helper to get proper state display name
// Exported: the crawl updates fallback needs the same key -> name conversion,
// since the crawl API matches 'IN', 'in' or 'Indiana' but not 'newyork'.
export const getStateDisplayName = (stateKey) => {
  if (!stateKey) return null;
  const normalized = stateKey.toLowerCase().replace(/[\s-_]/g, '');
  return STATE_NAMES[normalized] || stateKey;
};

// Helper to get storage keys based on report type and state
const getStorageKeys = (reportType, stateKey = null) => {
  // For state reports, include the state in the key to separate storage per state
  const suffix = reportType === 'state' && stateKey 
    ? `${reportType}_${stateKey.toLowerCase().replace(/[\s-_]/g, '')}` 
    : reportType;
  
  return {
    reports: `${REPORT_STORAGE_KEY_PREFIX}_${suffix}`,
    lastTime: `${LAST_REPORT_TIME_KEY_PREFIX}_${suffix}`
  };
};

// Helper to clean AI preamble and follow-up suggestions from content
function cleanAISuggestions(content) {
  if (!content) return content;
  
  let cleaned = content;
  
  // Patterns for AI preamble/intro (at the start)
  const preamblePatterns = [
    /^Here (?:are|is) (?:some|a|the)[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^Below (?:are|is)[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^I found[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^Based on[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^The following[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^For official[\s\S]*?(?:\.\[\d+\])+\s*/i,
    /^For (?:local|city|government|federal|state)[\s\S]*?(?:\.\[\d+\])+\s*/i,
  ];
  
  // Patterns for AI follow-up suggestions (at the end)
  const suggestionPatterns = [
    /If you(?:'d like| want),? (?:I can|you can|let me know)[\s\S]*$/i,
    /Would you like me to[\s\S]*$/i,
    /Let me know if you(?:'d like| want)[\s\S]*$/i,
    /I can (?:also |further )?(?:focus|narrow|filter|provide|help|drill)[\s\S]*$/i,
    /Do you want me to[\s\S]*$/i,
    /Want me to[\s\S]*$/i,
    /If you need[\s\S]*$/i,
    /If there's a specific[\s\S]*$/i,
    /If there is a specific[\s\S]*$/i,
    /You can also[\s\S]*$/i,
    /For (?:more|additional|further) (?:details|information|updates)[\s\S]*$/i,
    /If you(?:'d like| want| need) (?:the latest|more|to|me to)[\s\S]*$/i,
  ];
  
  // Remove preamble
  for (const pattern of preamblePatterns) {
    cleaned = cleaned.replace(pattern, '').trim();
  }
  
  // Remove suggestions
  for (const pattern of suggestionPatterns) {
    cleaned = cleaned.replace(pattern, '').trim();
  }
  
  // Clean up extra whitespace/newlines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  
  return cleaned;
}

// Helper to format time remaining
function formatTimeRemaining(ms) {
  if (ms <= 0) return 'now';
  
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  } else {
    return 'less than a minute';
  }
}

function formatReportDate(date) {
  const options = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  };
  return date.toLocaleDateString('en-US', options);
}

/**
 * @param {string} reportType - 'federal', 'state', or 'local'
 * @param {string} stateKey - Required for 'state' type reports (e.g., 'indiana', 'california')
 */
const usePersonalizedReport = (reportType = 'local', stateKey = null) => {
  // Get storage keys - now includes stateKey for state-specific storage
  const storageKeys = getStorageKeys(reportType, stateKey);
  
  const [currentReport, setCurrentReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [trackedLocation, setTrackedLocation] = useState(() => {
    if (reportType === 'local') return localStorage.getItem('homeCity');
    if (reportType === 'state') return stateKey || localStorage.getItem('homeState');
    return 'federal'; // Federal doesn't change
  });
  const [timeUntilNextReport, setTimeUntilNextReport] = useState(null);
  
  const lastGeneratedLocationRef = useRef(null);
  const timerRef = useRef(null);

  // Calculate time until next report
  const calculateTimeUntilNext = useCallback(() => {
    const lastReportTime = localStorage.getItem(storageKeys.lastTime);
    if (!lastReportTime) return 0;
    
    const nextReportTime = parseInt(lastReportTime, 10) + TWENTY_FOUR_HOURS_MS;
    const remaining = nextReportTime - Date.now();
    return Math.max(0, remaining);
  }, [storageKeys.lastTime]);

  // Update the countdown timer
  const updateTimeRemaining = useCallback(() => {
    const remaining = calculateTimeUntilNext();
    setTimeUntilNextReport(formatTimeRemaining(remaining));
  }, [calculateTimeUntilNext]);

  // Get user location data from localStorage
  const getUserLocationData = useCallback(() => {
    const homeCountry = localStorage.getItem('homeCountry');
    const homeState = localStorage.getItem('homeState');
    const homeStateName = localStorage.getItem('homeStateName');
    const homeCity = localStorage.getItem('homeCity');
    const homeCityState = localStorage.getItem('homeCityState');
    const homeCityStateName = localStorage.getItem('homeCityStateName');
    const homeCityData = localStorage.getItem('homeCityData');

    let cityData = null;
    if (homeCityData) {
      try {
        cityData = JSON.parse(homeCityData);
      } catch (e) {
        console.error('Failed to parse homeCityData:', e);
      }
    }

    return {
      country: homeCountry,
      state: homeState,
      stateName: homeStateName,
      city: homeCity,
      cityState: homeCityState,
      cityStateName: homeCityStateName,
      cityData
    };
  }, []);

  // Get the appropriate query based on report type
  const getQueryForReportType = useCallback((locationData) => {
    switch (reportType) {
      case 'federal':
        return 'United States government news. Do not respond with intro.';
      case 'state':
        // Use proper state name lookup
        const stateName = stateKey 
          ? getStateDisplayName(stateKey)
          : (locationData.stateName || getStateDisplayName(locationData.state) || locationData.state);
        if (!stateName) return null;
        return `${stateName} government news. Do not respond with intro.`;
      case 'local':
      default:
        return 'government news. Do not respond with intro.';
    }
  }, [reportType, stateKey]);

  // Get location identifier for tracking changes
  const getLocationIdentifier = useCallback((locationData) => {
    switch (reportType) {
      case 'federal':
        return 'federal';
      case 'state':
        return stateKey || locationData.state;
      case 'local':
      default:
        return locationData.city;
    }
  }, [reportType, stateKey]);

  // Get the display title for the report
  const getReportTitle = useCallback((locationData) => {
    switch (reportType) {
      case 'federal':
        return 'United States';
      case 'state':
        return stateKey 
          ? getStateDisplayName(stateKey)
          : (locationData.stateName || getStateDisplayName(locationData.state) || locationData.state);
      case 'local':
      default:
        return locationData.city;
    }
  }, [reportType, stateKey]);

  // Load report history from localStorage
  const loadReportHistory = useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKeys.reports);
      if (stored) {
        const reports = JSON.parse(stored);
        return reports.sort((a, b) => b.timestamp - a.timestamp);
      }
    } catch (e) {
      console.error('Failed to load report history:', e);
    }
    return [];
  }, [storageKeys.reports]);

  // Save report to localStorage
  const saveReport = useCallback((report) => {
    try {
      const history = loadReportHistory();
      history.unshift(report);
      const trimmedHistory = history.slice(0, 30);
      localStorage.setItem(storageKeys.reports, JSON.stringify(trimmedHistory));
      localStorage.setItem(storageKeys.lastTime, report.timestamp.toString());
      return trimmedHistory;
    } catch (e) {
      console.error('Failed to save report:', e);
      return loadReportHistory();
    }
  }, [loadReportHistory, storageKeys.reports, storageKeys.lastTime]);

  // Check if we need a new report
  const needsNewReport = useCallback(() => {
    const lastReportTime = localStorage.getItem(storageKeys.lastTime);
    if (!lastReportTime) return true;
    
    const timeSinceLastReport = Date.now() - parseInt(lastReportTime, 10);
    return timeSinceLastReport >= TWENTY_FOUR_HOURS_MS;
  }, [storageKeys.lastTime]);

  // Fetch news data for the report
  const fetchNews = useCallback(async (locationData) => {
    const query = getQueryForReportType(locationData);
    if (!query) return null;

    let url;
    
    if (reportType === 'local') {
      // Local uses localities parameter
      if (!locationData.cityData || !locationData.cityData.name || !locationData.cityData.state) {
        return null;
      }

      const localities = [{
        name: locationData.cityData.name,
        state: locationData.cityData.state,
        type: locationData.cityData.type || 'City'
      }];

      const localitiesParam = encodeURIComponent(JSON.stringify(localities));
      const queryParam = encodeURIComponent(query);
      url = `https://search.govglance.org/api/public-search?query=${queryParam}&localities=${localitiesParam}`;
    } else {
      // Federal and State use simple query
      const queryParam = encodeURIComponent(query);
      url = `https://search.govglance.org/api/public-search?query=${queryParam}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Clean up AI suggestions from content
    if (data.content) {
      data.content = cleanAISuggestions(data.content);
    }

    return data;
  }, [reportType, getQueryForReportType]);

  // Generate a new personalized report
  const generateReport = useCallback(async (forceGenerate = false) => {
    if (!forceGenerate && !needsNewReport()) {
      return null;
    }

    setGenerating(true);
    setError(null);

    try {
      const locationData = getUserLocationData();
      
      // Check if we have required data for this report type
      if (reportType === 'local' && !locationData.cityData) {
        setGenerating(false);
        return null;
      }
      
      if (reportType === 'state' && !stateKey && !locationData.state) {
        setGenerating(false);
        return null;
      }

      const timestamp = Date.now();
      const reportDate = new Date(timestamp);
      
      const newsData = await fetchNews(locationData);

      const locationIdentifier = getLocationIdentifier(locationData);
      const reportTitle = getReportTitle(locationData);

      const report = {
        id: `report_${reportType}_${timestamp}`,
        timestamp,
        date: reportDate.toISOString(),
        formattedDate: formatReportDate(reportDate),
        reportType,
        reportTitle,
        location: {
          country: locationData.country,
          state: locationData.state,
          stateName: locationData.stateName,
          city: locationData.city,
          cityState: locationData.cityState,
          cityStateName: locationData.cityStateName,
          identifier: locationIdentifier
        },
        news: newsData?.citations?.slice(0, 10) || [],
        content: newsData?.content || null
      };

      const updatedHistory = saveReport(report);
      setReportHistory(updatedHistory);
      setCurrentReport(report);
      
      // Update time remaining after new report
      updateTimeRemaining();
      
      return report;
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
      return null;
    } finally {
      setGenerating(false);
    }
  }, [needsNewReport, getUserLocationData, fetchNews, saveReport, updateTimeRemaining, reportType, stateKey, getLocationIdentifier, getReportTitle]);

  // Delete a report from history
  const deleteReport = useCallback((reportId) => {
    try {
      const history = loadReportHistory();
      const filtered = history.filter(r => r.id !== reportId);
      localStorage.setItem(storageKeys.reports, JSON.stringify(filtered));
      setReportHistory(filtered);
      
      if (currentReport?.id === reportId) {
        setCurrentReport(filtered[0] || null);
      }
    } catch (e) {
      console.error('Failed to delete report:', e);
    }
  }, [loadReportHistory, currentReport, storageKeys.reports]);

  // Clear all report history
  const clearHistory = useCallback(() => {
    localStorage.removeItem(storageKeys.reports);
    localStorage.removeItem(storageKeys.lastTime);
    setReportHistory([]);
    setCurrentReport(null);
    setTimeUntilNextReport(null);
  }, [storageKeys.reports, storageKeys.lastTime]);

  // Get a specific report by ID
  const getReportById = useCallback((reportId) => {
    const history = loadReportHistory();
    return history.find(r => r.id === reportId) || null;
  }, [loadReportHistory]);

  // Sync state from localStorage (used for cross-tab sync)
  const syncFromStorage = useCallback(() => {
    const history = loadReportHistory();
    setReportHistory(history);
    
    if (history.length > 0) {
      const locationData = getUserLocationData();
      const currentLocationId = getLocationIdentifier(locationData);
      const mostRecentReport = history[0];
      
      // Update current report if it matches current location or if no location-specific check needed
      if (reportType === 'federal' || mostRecentReport.location?.identifier === currentLocationId) {
        setCurrentReport(mostRecentReport);
      }
    }
    
    updateTimeRemaining();
  }, [loadReportHistory, updateTimeRemaining, getUserLocationData, getLocationIdentifier, reportType]);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      
      const history = loadReportHistory();
      setReportHistory(history);
      
      const locationData = getUserLocationData();
      const currentLocationId = getLocationIdentifier(locationData);
      
      // Initialize time remaining
      updateTimeRemaining();
      
      if (history.length > 0) {
        const mostRecentReport = history[0];
        
        // Check if the most recent report matches current location (or is federal which always matches)
        const locationMatches = reportType === 'federal' || 
          mostRecentReport.location?.identifier === currentLocationId;
        
        if (locationMatches) {
          // Same location - show existing report immediately
          setCurrentReport(mostRecentReport);
          lastGeneratedLocationRef.current = currentLocationId;
          
          // Check if 24 hours have passed and generate new report
          if (needsNewReport()) {
            // Don't block UI - generate in background
            setLoading(false);
            await generateReport(true);
            return;
          }
        } else if (currentLocationId) {
          // Location changed since last report, generate new
          lastGeneratedLocationRef.current = currentLocationId;
          await generateReport(true);
        } else {
          // No current location set, show most recent report
          setCurrentReport(mostRecentReport);
        }
      } else if (reportType === 'federal' || currentLocationId) {
        // No history exists, generate first report
        lastGeneratedLocationRef.current = currentLocationId;
        await generateReport(true);
      }

      setLoading(false);
    };

    initialize();
  }, [reportType, stateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up countdown timer (updates every minute)
  useEffect(() => {
    // Update immediately
    updateTimeRemaining();
    
    // Update every minute
    timerRef.current = setInterval(() => {
      updateTimeRemaining();
    }, 60 * 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [updateTimeRemaining]);

  // Listen for storage changes (cross-tab sync AND same-tab manual dispatches)
  useEffect(() => {
    const handleStorageChange = async (event) => {
      // Real StorageEvents have event.key, manually dispatched events don't
      const isManualDispatch = event.key === undefined;
      
      // Handle report history changes from other tabs
      if (event.key === storageKeys.reports || event.key === storageKeys.lastTime) {
        syncFromStorage();
        return;
      }
      
      // Handle location changes based on report type
      const relevantKey = reportType === 'local' ? 'homeCity' : 
                          reportType === 'state' ? 'homeState' : null;
      
      if (relevantKey && (event.key === relevantKey || isManualDispatch)) {
        const locationData = getUserLocationData();
        const newLocationId = getLocationIdentifier(locationData);
        const lastLocationId = lastGeneratedLocationRef.current;
        
        setTrackedLocation(newLocationId);
        
        if (newLocationId && newLocationId !== lastLocationId) {
          lastGeneratedLocationRef.current = newLocationId;
          setLoading(true);
          setCurrentReport(null);
          
          try {
            await generateReport(true);
          } catch (err) {
            setError(err.message);
          }
          
          setLoading(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [reportType, storageKeys, syncFromStorage, getUserLocationData, getLocationIdentifier, generateReport]);

  // Check if we have the required location data for this report type
  const hasRequiredLocationData = useCallback(() => {
    const locationData = getUserLocationData();
    switch (reportType) {
      case 'federal':
        return true; // Federal always has data
      case 'state':
        return !!(stateKey || locationData.state);
      case 'local':
      default:
        return !!locationData.cityData;
    }
  }, [reportType, stateKey, getUserLocationData]);

  return {
    currentReport,
    reportHistory,
    loading,
    generating,
    error,
    reportType,
    generateReport,
    deleteReport,
    clearHistory,
    getReportById,
    needsNewReport: needsNewReport(),
    timeUntilNextReport,
    hasLocationData: hasRequiredLocationData(),
    trackedLocation
  };
};

export default usePersonalizedReport;